import { Routes, Route } from 'react-router-dom'
import { useEffect } from 'react'
import { App as CapacitorApp } from '@capacitor/app'
import { StatusBar } from '@capacitor/status-bar'
import { Keyboard } from '@capacitor/keyboard'
import { SplashScreen } from '@capacitor/splash-screen'
import { setupMobileInputAutoScroll } from '@/utils/mobile-focus'

// Pages
import HomePage from '@/pages/HomePage'
import LoginPage from '@/pages/LoginPage'
import RegisterPage from '@/pages/RegisterPage'
import ProfilePage from '@/pages/ProfilePage'
import SettingsPage from '@/pages/SettingsPage'
import LobbyPage from '@/pages/LobbyPage'
import GamePage from '@/pages/GamePage'
import VotingPage from '@/pages/VotingPage'
import ResultsPage from '@/pages/ResultsPage'
import UserWordPage from '@/pages/UserWordPage'
import ForgotPasswordPage from '@/pages/ForgotPasswordPage'
import PasswordResetPage from '@/pages/PasswordResetPage'
import AuthCallbackPage from '@/pages/AuthCallbackPage'

// Layout
import Layout from '@/components/Layout'
import RequireAuth from '@/components/RequireAuth'

function App() {
  useEffect(() => {
    // Capacitor app initialization
    const initializeApp = async () => {
      try {
        // Check if we're running in a native app
        const { Capacitor } = await import('@capacitor/core')
        
        if (Capacitor.isNativePlatform()) {
          // Prefer overlay instead of setting colors directly (edge-to-edge)
          try { await StatusBar.setOverlaysWebView({ overlay: true }) } catch {}
          
          // Configure keyboard (only on native platforms)
          await Keyboard.setAccessoryBarVisible({ isVisible: true })
          
          // Handle app state changes (only on native platforms)
          CapacitorApp.addListener('appStateChange', ({ isActive }) => {
            console.log('App state changed. Is active?', isActive)
          })

          // Handle OAuth deep links (scraible://auth/callback?code=...)
          CapacitorApp.addListener('appUrlOpen', async (data: { url: string }) => {
            try {
              const url = new URL(data.url)
              if (url.hostname === 'auth' && url.pathname === '/callback') {
                const code = url.searchParams.get('code')
                if (code) {
                  const { supabase } = await import('@/config/supabase')
                  const { error } = await supabase.auth.exchangeCodeForSession(code)
                  if (error) throw error
                }
              }
            } catch (e) {
              console.error('appUrlOpen handler error', e)
            }
          })

          // Uygulama hazır olduğunda Splash Screen'i yumuşakça kapat
          await SplashScreen.hide({
            fadeOutDuration: 500
          });
        }
      } catch (error) {
        // Silently ignore Capacitor errors in browser
        console.debug('Capacitor not available:', error)
      }
    }

    initializeApp()

    // Mobile input auto-scroll (Capacitor)
    const cleanupAutoScroll = setupMobileInputAutoScroll()

    return () => {
      // Only remove listeners if they were added
      try {
        CapacitorApp.removeAllListeners()
      } catch (error) {
        console.debug('No Capacitor listeners to remove')
      }
      cleanupAutoScroll()
    }
  }, [])

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/auth/reset-password" element={<PasswordResetPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/profile" element={<RequireAuth><ProfilePage /></RequireAuth>} />
        <Route path="/settings" element={<RequireAuth><SettingsPage /></RequireAuth>} />
        <Route path="/lobby/:roomId" element={<LobbyPage />} />
        <Route path="/userword/:roomId" element={<UserWordPage />} />
        <Route path="/game/:roomId" element={<GamePage />} />
        <Route path="/voting/:roomId" element={<VotingPage />} />
        <Route path="/results/:roomId" element={<ResultsPage />} />
      </Routes>
    </Layout>
  )
}

export default App